import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Database, ArrowRight, CheckCircle } from "lucide-react"

export function NestJSOverviewPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4 py-8">
        <Badge variant="secondary" className="text-base">
          NestJS Templates
        </Badge>
        <h1 className="text-4xl font-bold tracking-tight">
          Choose Your Database
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Both templates include complete authentication, OAuth, and production-ready features. 
          The only difference is the database layer - choose based on your data needs.
        </p>
      </div>

      {/* Template Comparison */}
      <div className="grid md:grid-cols-2 gap-6">
        <TemplateCard
          title="MongoDB Template"
          description="Flexible, document-based data modeling"
          database="MongoDB + Mongoose"
          link="/nestjs/mongodb"
          features={[
            "Schema-based models with Mongoose ODM",
            "No migrations needed (schemaless)",
            "Flexible data structure",
            "ObjectId with auto transformation",
            "Great for rapid prototyping",
            "MongoDB Atlas compatible",
          ]}
          useCases={[
            "Content management systems",
            "Real-time applications",
            "IoT and sensor data",
            "Flexible data requirements",
            "Rapid prototyping",
          ]}
        />

        <TemplateCard
          title="MySQL Template"
          description="Relational, structured data management"
          database="MySQL + TypeORM"
          link="/nestjs/mysql"
          features={[
            "Entity-based models with TypeORM",
            "Migration system for version control",
            "Strong relational support",
            "UUID primary keys",
            "ACID compliance",
            "Production-grade reliability",
          ]}
          useCases={[
            "E-commerce platforms",
            "Financial applications",
            "Complex data relationships",
            "Strict data consistency",
            "Enterprise applications",
          ]}
        />
      </div>

      {/* Shared Features */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Shared Features</CardTitle>
          <CardDescription>
            Both templates include the exact same features, differing only in the database layer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharedFeatures.map((feature, index) => (
              <div key={index} className="flex gap-3">
                <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm">{feature.title}</h4>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Decision Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Decision Guide</CardTitle>
          <CardDescription>
            Not sure which one to choose? Here's a quick guide
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <DecisionRow
              scenario="You're building an MVP quickly"
              recommendation="MongoDB"
              reason="Flexible schema allows rapid iteration without migrations"
            />
            <DecisionRow
              scenario="You need complex relationships and joins"
              recommendation="MySQL"
              reason="TypeORM provides excellent relational support with foreign keys"
            />
            <DecisionRow
              scenario="Your data structure changes frequently"
              recommendation="MongoDB"
              reason="No migration files to manage, easy schema evolution"
            />
            <DecisionRow
              scenario="You need strict data consistency"
              recommendation="MySQL"
              reason="ACID compliance ensures data integrity"
            />
            <DecisionRow
              scenario="You're building a financial app"
              recommendation="MySQL"
              reason="Transactions and relational integrity are critical"
            />
            <DecisionRow
              scenario="You have nested/hierarchical data"
              recommendation="MongoDB"
              reason="Document model handles nested structures naturally"
            />
          </div>
        </CardContent>
      </Card>

      {/* API Compatibility */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">100% API Compatible</CardTitle>
          <CardDescription>
            Switch between templates without changing your frontend
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Both templates expose the exact same REST API endpoints with identical request/response formats. 
            This means you can:
          </p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-primary shrink-0" />
              <span>Start with one database and switch later if needed</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-primary shrink-0" />
              <span>Use the same frontend code with both templates</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-primary shrink-0" />
              <span>Test both and choose what works best for your use case</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle className="h-5 w-5 text-primary shrink-0" />
              <span>Learn one, understand both - only database layer differs</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}

const sharedFeatures = [
  {
    title: "JWT Authentication",
    description: "Access & refresh tokens with automatic rotation",
  },
  {
    title: "OAuth Integration",
    description: "Google OAuth 2.0 (extensible to other providers)",
  },
  {
    title: "Role-Based Access",
    description: "USER, ADMIN, SUPER_ADMIN roles",
  },
  {
    title: "Password Security",
    description: "bcrypt hashing with 12 salt rounds",
  },
  {
    title: "Global Response Format",
    description: "Consistent API response structure",
  },
  {
    title: "Request Validation",
    description: "class-validator for all endpoints",
  },
  {
    title: "API Documentation",
    description: "Swagger & Scalar auto-generated docs",
  },
  {
    title: "TypeScript Strict",
    description: "Full type safety across the codebase",
  },
  {
    title: "Environment Config",
    description: "Type-safe configuration system",
  },
]

function TemplateCard({
  title,
  description,
  database,
  link,
  features,
  useCases,
}: {
  title: string
  description: string
  database: string
  link: string
  features: string[]
  useCases: string[]
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between mb-3">
          <Database className="h-10 w-10 text-primary" />
          <Badge>{database}</Badge>
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-6">
        <div className="space-y-3 flex-1">
          <h4 className="font-semibold text-sm">Key Features</h4>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <h4 className="font-semibold text-sm">Best For</h4>
          <ul className="space-y-2">
            {useCases.map((useCase, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="text-primary">•</span>
                {useCase}
              </li>
            ))}
          </ul>
        </div>

        <Button asChild className="w-full" size="lg">
          <Link to={link} className="flex items-center justify-center gap-2">
            View Documentation
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function DecisionRow({
  scenario,
  recommendation,
  reason,
}: {
  scenario: string
  recommendation: string
  reason: string
}) {
  return (
    <div className="flex gap-4 items-start p-4 bg-muted/30 rounded-lg">
      <div className="flex-1 space-y-1">
        <p className="font-semibold">{scenario}</p>
        <p className="text-sm text-muted-foreground">{reason}</p>
      </div>
      <Badge variant={recommendation === "MongoDB" ? "default" : "secondary"} className="shrink-0">
        {recommendation}
      </Badge>
    </div>
  )
}

